import React, { useEffect, useState, useCallback } from 'react';
import { Send, Activity } from 'react-iconly';
import {
  getBrief, getPMUpdates, getMilestones, generatePlan, postPMMessage, createMilestones, formatIDR,
} from '../services/portalService';

const AIProjectManager = ({ order, onMilestonesChanged }) => {
  const [brief, setBrief] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [plan, setPlan] = useState(null);
  const [hasMilestones, setHasMilestones] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingMs, setSavingMs] = useState(false);
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    const [b, u, ms] = await Promise.all([getBrief(order.id), getPMUpdates(order.id), getMilestones(order.id)]);
    setBrief(b); setUpdates(u); setHasMilestones(ms.length > 0);
    // Surface the most recent AI plan (with milestones) if present.
    const lastPlan = [...u].reverse().find((x) => x.kind === 'plan' && x.meta && x.meta.milestones);
    if (lastPlan) setPlan(lastPlan.meta);
    setLoading(false);
  }, [order.id]);

  useEffect(() => { reload(); }, [reload]);

  const briefContext = (extra) => {
    const base = brief?.content ? JSON.stringify(brief.content, null, 2) : '(brief belum diisi)';
    return extra ? `${base}\n\n${extra}` : base;
  };

  const generate = async () => {
    setGenerating(true); setError('');
    try {
      const res = await generatePlan({
        orderId: order.id, brief: briefContext(),
        packageName: order.package_name, total: order.total_amount,
        plan: order.payment_plan, status: order.status,
      });
      setPlan(res.plan || { summary: res.text });
      await reload();
    } catch (e) { setError(e.message || 'Could not reach the AI manager'); }
    setGenerating(false);
  };

  const saveMilestones = async () => {
    if (!plan?.milestones?.length) return;
    setSavingMs(true); setError('');
    try {
      await createMilestones(order.id, plan.milestones.map((m, i) => ({
        sequence: i + 1, title: m.title, description: m.description, amount: m.amount,
        due_date: m.due_in_days ? new Date(Date.now() + m.due_in_days * 864e5).toISOString().slice(0, 10) : null,
      })));
      setHasMilestones(true);
      onMilestonesChanged && onMilestonesChanged();
    } catch (e) { setError(e.message || 'Could not save milestones'); }
    setSavingMs(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const question = msg.trim();
    setSending(true); setError(''); setMsg('');
    try {
      await postPMMessage(order.id, question);
      await generatePlan({
        orderId: order.id,
        brief: briefContext(`Pertanyaan/permintaan terbaru dari klien: "${question}". Jawab langsung dan perbarui ringkasan bila perlu.`),
        packageName: order.package_name, total: order.total_amount, plan: order.payment_plan, status: order.status,
      });
      await reload();
    } catch (e2) { setError(e2.message || 'Message failed'); }
    setSending(false);
  };

  if (loading) return <div className="portal-spinner" />;

  return (
    <div>
      <div className="portal-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Activity set="bulk" size={22} primaryColor="#FF7722" />
            <div>
              <div style={{ fontWeight: 800, fontFamily: 'var(--v2-font-display)', fontSize: 18 }}>AI Project Manager</div>
              <div className="portal-muted">Turns your brief into a plan, milestones, and next steps.</div>
            </div>
          </div>
          <button className="portal-btn portal-btn-primary" onClick={generate} disabled={generating}>
            {generating ? 'Thinking…' : plan ? 'Refresh plan' : 'Generate plan'}
          </button>
        </div>

        {plan && (
          <div style={{ marginTop: 20 }}>
            {plan.summary && <p style={{ fontSize: 14, lineHeight: 1.6 }}>{plan.summary}</p>}

            {Array.isArray(plan.milestones) && plan.milestones.length > 0 && (
              <>
                <p className="portal-label" style={{ margin: '18px 0 8px' }}>Proposed milestones</p>
                {plan.milestones.map((m, i) => (
                  <div key={i} className="portal-row">
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{i + 1}. {m.title}</div>
                      {m.description && <div className="portal-muted">{m.description}</div>}
                    </div>
                    <div className="portal-amount">{formatIDR(m.amount)}</div>
                  </div>
                ))}
                {order.payment_plan === 'milestone' && !hasMilestones && (
                  <button className="portal-btn portal-btn-dark" style={{ marginTop: 14 }} onClick={saveMilestones} disabled={savingMs}>
                    {savingMs ? 'Saving…' : 'Use these milestones (enable per-milestone payment)'}
                  </button>
                )}
                {hasMilestones && <p className="portal-muted" style={{ marginTop: 12 }}>✓ Milestones saved — pay them in the Billing tab.</p>}
              </>
            )}

            {Array.isArray(plan.next_steps) && plan.next_steps.length > 0 && (
              <>
                <p className="portal-label" style={{ margin: '18px 0 8px' }}>Next steps</p>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.7 }}>
                  {plan.next_steps.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
          </div>
        )}
        {!plan && <p className="portal-muted" style={{ marginTop: 16 }}>No plan yet. Fill in your brief, then generate a plan.</p>}
        {error && <p style={{ color: 'var(--v2-color-error)', fontSize: 12, marginTop: 12 }}>{error}</p>}
      </div>

      {/* Conversation */}
      <p className="portal-label" style={{ marginBottom: 10 }}>Conversation</p>
      <div className="portal-card">
        {updates.length === 0 ? (
          <p className="portal-muted">Ask your project manager anything about scope, timeline, or progress.</p>
        ) : (
          <div className="pm-log">
            {updates.map((u) => (
              <div key={u.id} className={`pm-msg ${u.role === 'client' ? 'client' : 'ai'}`}>
                <div className="pm-msg-role">{u.role === 'client' ? 'You' : 'AI Manager'}</div>
                {u.content}
              </div>
            ))}
          </div>
        )}
        <form onSubmit={send} style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <input className="portal-input" placeholder="Ask the manager… (e.g. can we add a blog?)"
            value={msg} onChange={(e) => setMsg(e.target.value)} disabled={sending} />
          <button className="portal-btn portal-btn-dark" type="submit" disabled={sending || !msg.trim()}>
            <Send set="bold" size={16} /> {sending ? '…' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AIProjectManager;
